import { useEffect, useState } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { 
  MessageSquare, 
  Brain, 
  Upload, 
  BookOpen, 
  Sparkles, 
  Dumbbell,
  Trophy,
  Star,
  Unlock
} from 'lucide-react';

const iconMap: Record<string, React.ElementType> = {
  MessageSquare,
  Brain,
  Upload,
  BookOpen,
  Sparkles,
  Dumbbell,
};

export function MilestoneCelebration() {
  const { pendingCelebration, clearCelebration } = useOnboarding();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (pendingCelebration) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [pendingCelebration]);

  if (!pendingCelebration) return null;

  const { milestone } = pendingCelebration;
  const IconComponent = iconMap[milestone.icon] || Star;

  return (
    <Dialog open={!!pendingCelebration} onOpenChange={() => clearCelebration()}>
      <DialogContent className="sm:max-w-md text-center" data-testid="dialog-milestone-celebration">
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-bounce"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 50}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${0.5 + Math.random() * 0.5}s`,
                }}
              >
                <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col items-center gap-4 py-4">
          <div className="relative">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
              <Trophy className="w-10 h-10 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-green-500 flex items-center justify-center border-2 border-background">
              <IconComponent className="w-4 h-4 text-white" />
            </div>
          </div>

          <div className="space-y-2">
            <h2 className="text-2xl font-bold">Achievement Unlocked!</h2>
            <Badge variant="secondary" className="text-base px-3 py-1">
              {milestone.title}
            </Badge>
          </div>

          <p className="text-muted-foreground max-w-sm">
            {milestone.celebrationMessage}
          </p>

          <div className="flex items-center gap-2 text-lg font-semibold text-yellow-600 dark:text-yellow-400">
            <Sparkles className="w-5 h-5" />
            +{milestone.xpReward} XP
          </div>

          {milestone.unlocksFeature && (
            <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400 bg-green-500/10 px-3 py-2 rounded-full">
              <Unlock className="w-4 h-4" />
              <span className="capitalize">{milestone.unlocksFeature}</span> is now unlocked!
            </div>
          )}

          <Button 
            onClick={clearCelebration} 
            className="mt-2"
            data-testid="button-continue-celebration"
          >
            Continue Your Journey
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
